// frontend/src/features/bapb/BAPBApp.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../apiClient";
import ImportWizard from "./ImportWizard";
import PacketBuilder from "./PacketBuilder";

const STATUS_OPTIONS = [
  "Draft",
  "Started",
  "Ready to Send",
  "Sent",
  "Toplined",
  "Settled",
  "Hearing",
];

const STATUS_COLORS = {
  Draft:          { bg: "bg-slate-100",      text: "text-slate-700",    ring: "ring-slate-200",      dot: "bg-slate-400" },
  Started:        { bg: "bg-violet-50",      text: "text-violet-700",   ring: "ring-violet-200",     dot: "bg-violet-400" },
  "Ready to Send":{ bg: "bg-sky-50",         text: "text-sky-700",      ring: "ring-sky-200",        dot: "bg-sky-400" },
  Sent:           { bg: "bg-indigo-50",       text: "text-indigo-700",   ring: "ring-indigo-200",     dot: "bg-indigo-400" },
  Toplined:       { bg: "bg-amber-50",        text: "text-amber-700",    ring: "ring-amber-200",      dot: "bg-amber-400" },
  Settled:        { bg: "bg-emerald-50",      text: "text-emerald-700",  ring: "ring-emerald-200",    dot: "bg-emerald-400" },
  Hearing:        { bg: "bg-rose-50",         text: "text-rose-700",     ring: "ring-rose-200",       dot: "bg-rose-400" },
};

const PAGE_SIZE = 50;
const MAX_RESULTS = 1000;

export default function BAPBApp({ email, onClose, logout }) {
  const [mode, setMode] = useState("home"); // home | import | builder
  const [packet, setPacket] = useState(null);

  const navigate = useNavigate();
  const goApps = () => {
    if (typeof onClose === "function") onClose();
    else navigate("/apps");
  };

  // ---------------- Home (Search) state ----------------
  const [create, setCreate] = useState({
    account_number: "",
    tax_year: new Date().getFullYear(),
    title: ""
  });
  const [showCreate, setShowCreate] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [q, setQ] = useState("");
  const [year, setYear] = useState("");
  const [status, setStatus] = useState([]);
  const [client, setClient] = useState("");
  const [city, setCity] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [assessor, setAssessor] = useState("");
  const [consultant, setConsultant] = useState("");

  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [lastParams, setLastParams] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [needsRequery, setNeedsRequery] = useState(false);
  const [viewMode, setViewMode] = useState("table"); // table | cards

  const [cols, setCols] = useState({
    assessor: true,
    consultant: true,
    client: true,
    statusCol: true,
    city: false,
    state: false,
    cadCode: false,
    siteName: false,
    units: false,
  });
  const [showColPicker, setShowColPicker] = useState(false);
  const toggleCol = (k) => setCols((p) => ({ ...p, [k]: !p[k] }));

  const searchRef = useRef(null);

  // Keyboard shortcut: "/" focuses search
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "/" && mode === "home" && !e.ctrlKey && !e.metaKey) {
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag === "input" || tag === "textarea" || tag === "select") return;
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [mode]);

  // Build params for API
  const searchParams = useMemo(() => {
    const p = {};
    if (q.trim()) p.q = q.trim();
    if (year) p.tax_year = year;
    if (status.length) p.status = status.join(",");
    if (client.trim()) p.client = client.trim();
    if (city.trim()) p.city = city.trim();
    if (stateCode.trim()) p.state = stateCode.trim();
    if (assessor.trim()) p.assessor = assessor.trim();
    if (consultant.trim()) p.consultant = consultant.trim();
    return p;
  }, [q, year, status, client, city, stateCode, assessor, consultant]);

  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (year) c++;
    if (status.length) c++;
    if (client.trim()) c++;
    if (city.trim()) c++;
    if (stateCode.trim()) c++;
    if (assessor.trim()) c++;
    if (consultant.trim()) c++;
    return c;
  }, [year, status, client, city, stateCode, assessor, consultant]);

  // --- helpers ---
  const norm = (s) => (s || "").toString().toLowerCase().trim();
  const includes = (hay, needle) => norm(hay).includes(norm(needle));

  function matchesSearch(row) {
    const cover = row._coverMeta || {};
    if (status.length && !status.includes(row.status)) return false;
    if (year && String(row.tax_year) !== String(year)) return false;
    if (client && !includes(cover.client_name, client)) return false;
    if (city && !includes(cover.city, city)) return false;
    if (stateCode && !includes(cover.state || row.state, stateCode)) return false;
    if (assessor && !includes(cover.assessor, assessor)) return false;
    if (consultant && !includes(cover.consultant, consultant)) return false;

    if (q.trim()) {
      const haystack = [
        row.account_number, row.title, row.status, row.tax_year,
        cover.site_name, cover.client_name, cover.city, cover.state,
        cover.assessor, cover.consultant, cover.cad_location_code,
        cover.address, cover.zip,
      ]
        .filter(Boolean)
        .map(String)
        .map((x) => x.toLowerCase())
        .join(" | ");
      if (!haystack.includes(q.trim().toLowerCase())) return false;
    }
    return true;
  }

  function extractCoverMetaFromPacket(p) {
    const sections = Array.isArray(p?.sections) ? p.sections : [];
    let latestCover = null;
    for (const s of sections) {
      if (String(s?.type || "").toLowerCase() !== "cover") continue;
      if (!latestCover || (s?.order_index ?? -1) >= (latestCover?.order_index ?? -1)) {
        latestCover = s;
      }
    }
    const pi = latestCover?.data?.property_info || {};
    return {
      assessor: pi.assessor || "",
      consultant: pi.consultant_name || pi.consultant || "",
      site_name: pi.site_name || "",
      units: pi.units || "",
      cad_location_code: pi.cad_location_code || "",
      city: pi.city || "",
      state: pi.state || "",
      zip: pi.zip || "",
      address: pi.address || "",
      client_name: pi.client_name || "",
    };
  }

  async function confirmFullScan(countParams = {}) {
    let total = null;
    try {
      const res = await api.get("/bapb/packets/count", { params: countParams });
      if (typeof res?.data?.total === "number") total = res.data.total;
    } catch {
      // ignore
    }
    const msg = total != null
      ? `By continuing, you'll load all ${total.toLocaleString()} packets and it may take a while. Continue?`
      : "By continuing, you'll load all packets and it may take a while. Continue?";
    return window.confirm(msg);
  }

  function dedupeByIdAppend(prev, incoming) {
    const seen = new Set(prev.map((x) => x.id || x.packet_id));
    const add = incoming.filter((x) => !seen.has(x.id || x.packet_id));
    return [...prev, ...add];
  }

  const runSearch = async () => {
    setIsSearching(true);
    setError("");
    setResults([]);
    setNextCursor(null);
    setLastParams(null);
    setPage(1);

    const hasFilters = Object.keys(searchParams || {}).length > 0;

    try {
      let params = { ...searchParams };
      if (!hasFilters) {
        const ok = await confirmFullScan({});
        if (!ok) {
          setIsSearching(false);
          return;
        }
        params.allowFullScan = true;
      }

      params.limit = 200;

      let base;
      try {
        base = await api.get("/bapb/packets", { params, timeout: 120000 });
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 422 && detail?.code === "NO_FILTERS_CONFIRMATION_REQUIRED") {
          const total = Number(detail?.total ?? 0);
          const ok = window.confirm(
            total
              ? `By continuing, you'll load all ${total.toLocaleString()} packets and it may take a while. Continue?`
              : "By continuing, you'll load all packets and it may take a while. Continue?"
          );
          if (!ok) {
            setIsSearching(false);
            return;
          }
          base = await api.get("/bapb/packets", {
            params: { ...params, allowFullScan: true, limit: 200 },
            timeout: 120000,
          });
        } else {
          const msg =
            typeof detail === "string"
              ? detail
              : detail?.message || e?.message || "Search failed";
          setError(String(msg));
          setIsSearching(false);
          return;
        }
      }

      const rows = Array.isArray(base?.data)
        ? base.data
        : Array.isArray(base?.data?.items)
        ? base.data.items
        : [];

      const nc = base?.headers?.["x-next-cursor"] || null;
      setNextCursor(nc);

      const enriched = rows.map((p) => ({
        ...p,
        _coverMeta: extractCoverMetaFromPacket(p),
      }));

      const filtered = enriched.filter(matchesSearch).slice(0, MAX_RESULTS);
      setResults(filtered);
      setLastParams(params);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : detail?.message || e?.message || "Search failed";
      setError(String(msg));
    } finally {
      setIsSearching(false);
    }
  };

  const loadMore = async () => {
    if (!nextCursor || !lastParams || isLoadingMore) return;
    if ((results?.length || 0) >= MAX_RESULTS) {
      setNextCursor(null);
      return;
    }

    setIsLoadingMore(true);
    setError("");

    try {
      const params = { ...lastParams, cursor: nextCursor, limit: 200 };
      const res = await api.get("/bapb/packets", { params, timeout: 120000 });

      const rows = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.items)
        ? res.data.items
        : [];

      const nc = res?.headers?.["x-next-cursor"] || null;

      const enriched = rows.map((p) => ({
        ...p,
        _coverMeta: extractCoverMetaFromPacket(p),
      }));

      setResults((prev) => {
        const next = dedupeByIdAppend(prev, enriched).filter(matchesSearch);
        const capped = next.slice(0, MAX_RESULTS);
        return capped;
      });

      const afterCount = Math.min((results?.length || 0) + enriched.length, MAX_RESULTS);
      if (afterCount >= MAX_RESULTS) setNextCursor(null);
      else setNextCursor(nc);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : detail?.message || e?.message || "Load more failed";
      setError(String(msg));
    } finally {
      setIsLoadingMore(false);
    }
  };

  async function ensureLoadedUpToMaxForExport() {
    if (!lastParams) return;
    let cursor = nextCursor;
    let safety = 0;

    while (cursor && (results?.length || 0) < MAX_RESULTS && safety < 50) {
      safety += 1;
      const params = { ...lastParams, cursor, limit: 200 };
      const res = await api.get("/bapb/packets", { params, timeout: 120000 });

      const rows = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.items)
        ? res.data.items
        : [];

      const nc = res?.headers?.["x-next-cursor"] || null;

      const enriched = rows.map((p) => ({
        ...p,
        _coverMeta: extractCoverMetaFromPacket(p),
      }));

      setResults((prev) => {
        const next = dedupeByIdAppend(prev, enriched).filter(matchesSearch);
        return next.slice(0, MAX_RESULTS);
      });

      cursor = nc;
      if ((results?.length || 0) >= MAX_RESULTS) {
        cursor = null;
      }
    }

    setNextCursor(cursor);
  }

  const exportResults = async () => {
    try {
      if (!results?.length) {
        alert("No results to export.");
        return;
      }
      if (!window.XLSX) {
        alert("XLSX library is not loaded (window.XLSX missing).");
        return;
      }

      setIsExporting(true);

      if (nextCursor && (results?.length || 0) < MAX_RESULTS) {
        await ensureLoadedUpToMaxForExport();
      }

      const rows = (results || []).slice(0, MAX_RESULTS);

      const data = rows.map((r) => {
        const cover = r._coverMeta || {};
        const property = r.title || cover.site_name || "";
        return {
          Property: property,
          Account: r.account_number || "",
          Assessor: cover.assessor || "",
          Consultant: cover.consultant || "",
          Client: cover.client_name || "",
          Address: cover.address || "",
          City: cover.city || "",
          State: cover.state || "",
          Zip: cover.zip || "",
        };
      });

      const ws = window.XLSX.utils.json_to_sheet(data);
      const wb = window.XLSX.utils.book_new();
      window.XLSX.utils.book_append_sheet(wb, ws, "BAPB Results");

      const stamp = new Date().toISOString().slice(0, 10);
      window.XLSX.writeFile(wb, `bapb_search_results_${stamp}.xlsx`);
    } catch (e) {
      alert(e?.message || "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const createBlank = async () => {
    const acct = String(create.account_number || "").trim();
    const yearNum = Number(create.tax_year);
    if (!acct) {
      alert("Please enter an Account # (parcel).");
      return;
    }
    if (!Number.isInteger(yearNum) || yearNum < 1900 || yearNum > 2100) {
      alert("Please enter a valid Tax Year (e.g., 2025).");
      return;
    }
    try {
      const body = {
        account_number: acct,
        tax_year: yearNum,
        title: create.title || acct,
        sections: [
          { type: "cover", order_index: 0, data: { property_info: { parcel: acct } } },
        ],
      };
      const res = await api.post("/bapb/packets", body);
      const p = res?.data;
      setPacket(p);
      setMode("builder");
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to create packet");
    }
  };

  const openPacket = (p) => {
    setPacket(p);
    setMode("builder");
    setNeedsRequery(true);
  };

  const goHome = () => {
    setPacket(null);
    setMode("home");
    if (needsRequery) {
      setResults([]);
      setError("");
      setIsSearching(false);
      setNeedsRequery(false);
    }
  };

  const clearAll = () => {
    setQ(""); setYear(""); setStatus([]); setClient(""); setCity("");
    setStateCode(""); setAssessor(""); setConsultant(""); setResults([]); setError("");
    setNextCursor(null); setLastParams(null); setPage(1);
  };

  const toggleStatus = (s) => {
    setStatus((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const totalPages = Math.max(1, Math.ceil((results?.length || 0) / PAGE_SIZE));
  const pageSafe = Math.min(Math.max(1, page), totalPages);
  const pageStart = (pageSafe - 1) * PAGE_SIZE;
  const pageRows = (results || []).slice(pageStart, pageStart + PAGE_SIZE);

  // ─────────────────────── RENDER ───────────────────────

  return (
    <div className="bapb-root">
      {/* ══════ HEADER ══════ */}
      <header className="bapb-header">
        <div className="bapb-header-inner">
          <div className="flex items-center gap-3">
            <img src="/assets/balogo.png" alt="BA Logo" className="bapb-logo-img" />
            <div>
              <div className="text-[15px] font-semibold tracking-tight text-gray-800">BAPB</div>
              <div className="text-[11px] text-gray-400 font-medium tracking-wide">PACKET BUILDER</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {mode !== "home" && (
              <button onClick={goHome} className="bapb-header-btn">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12l9-9 9 9"/><path d="M9 21V9h6v12"/></svg>
                Home
              </button>
            )}
            <button onClick={goApps} className="bapb-header-btn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
              Apps
            </button>
          </div>
        </div>
      </header>

      {/* ══════ MAIN ══════ */}
      <main className="bapb-main">
        {mode === "home" && (
          <>
            {/* ── COMMAND BAR ── */}
            <div className="bapb-command-bar">
              <div className="bapb-search-wrapper">
                <svg className="bapb-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
                </svg>
                <input
                  ref={searchRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runSearch()}
                  placeholder="Search packets by account, property, name, notes..."
                  className="bapb-search-input"
                />
                {q && (
                  <button onClick={() => setQ("")} className="bapb-search-clear" title="Clear">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
                  </button>
                )}
                <kbd className="bapb-kbd">/</kbd>
              </div>

              {/* Action buttons row */}
              <div className="bapb-action-row">
                <div className="flex items-center gap-2">
                  <button
                    onClick={runSearch}
                    disabled={isSearching}
                    className="bapb-btn-primary"
                  >
                    {isSearching ? (
                      <><span className="bapb-spinner" /> Searching...</>
                    ) : (
                      <>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                        Search
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`bapb-btn-ghost ${showFilters ? "bapb-btn-ghost-active" : ""}`}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>
                    Filters
                    {activeFilterCount > 0 && (
                      <span className="bapb-filter-badge">{activeFilterCount}</span>
                    )}
                  </button>

                  {(q || activeFilterCount > 0 || results.length > 0) && (
                    <button onClick={clearAll} className="bapb-btn-ghost">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
                      Clear all
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setMode("import")}
                    className="bapb-btn-accent"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    Import
                  </button>
                  <button
                    onClick={() => setShowCreate(!showCreate)}
                    className={`bapb-btn-ghost ${showCreate ? "bapb-btn-ghost-active" : ""}`}
                    title="Create single packet"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                    New
                  </button>
                </div>
              </div>
            </div>

            {/* ── CREATE (collapsible) ── */}
            {showCreate && (
              <div className="bapb-panel bapb-slide-down">
                <div className="bapb-panel-header">
                  <span>Create New Packet</span>
                  <button onClick={() => setShowCreate(false)} className="bapb-panel-close">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <div>
                    <label className="bapb-label">Account # (parcel)</label>
                    <input
                      value={create.account_number}
                      onChange={(e) => setCreate({ ...create, account_number: e.target.value })}
                      className="bapb-input"
                      placeholder="e.g., R12345"
                    />
                  </div>
                  <div>
                    <label className="bapb-label">Tax Year</label>
                    <input
                      type="number"
                      value={create.tax_year}
                      onChange={(e) => setCreate({ ...create, tax_year: e.target.value })}
                      className="bapb-input"
                      placeholder="2025"
                    />
                  </div>
                  <div>
                    <label className="bapb-label">Site Name</label>
                    <input
                      value={create.title}
                      onChange={(e) => setCreate({ ...create, title: e.target.value })}
                      className="bapb-input"
                      placeholder="Label for this packet"
                    />
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <button className="bapb-btn-primary" onClick={createBlank}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                    Create & Open
                  </button>
                </div>
              </div>
            )}

            {/* ── ADVANCED FILTERS (collapsible) ── */}
            {showFilters && (
              <div className="bapb-panel bapb-slide-down">
                <div className="bapb-panel-header">
                  <span>Advanced Filters</span>
                  <button onClick={() => setShowFilters(false)} className="bapb-panel-close">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
                  </button>
                </div>

                {/* Status pills */}
                <div className="mt-3">
                  <label className="bapb-label mb-2">Status</label>
                  <div className="flex flex-wrap gap-2">
                    {STATUS_OPTIONS.map((s) => {
                      const active = status.includes(s);
                      const c = STATUS_COLORS[s] || STATUS_COLORS.Draft;
                      return (
                        <button
                          key={s}
                          onClick={() => toggleStatus(s)}
                          className={[
                            "bapb-status-pill",
                            active ? `${c.bg} ${c.text} ring-1 ${c.ring}` : "bg-gray-100 text-gray-400 hover:text-gray-600 hover:bg-gray-200",
                          ].join(" ")}
                        >
                          <span className={`inline-block w-2 h-2 rounded-full ${active ? c.dot : "bg-gray-300"}`} />
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-4">
                  <div>
                    <label className="bapb-label">Tax Year</label>
                    <input type="number" value={year} onChange={(e) => setYear(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} className="bapb-input" placeholder="2025" />
                  </div>
                  <div>
                    <label className="bapb-label">Client</label>
                    <input value={client} onChange={(e) => setClient(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} className="bapb-input" />
                  </div>
                  <div>
                    <label className="bapb-label">Assessor</label>
                    <input value={assessor} onChange={(e) => setAssessor(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} className="bapb-input" />
                  </div>
                  <div>
                    <label className="bapb-label">Consultant</label>
                    <input value={consultant} onChange={(e) => setConsultant(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} className="bapb-input" />
                  </div>
                  <div>
                    <label className="bapb-label">State</label>
                    <input value={stateCode} onChange={(e) => setStateCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} className="bapb-input" placeholder="TX" />
                  </div>
                </div>
              </div>
            )}

            {/* ── RESULTS ── */}
            <div className="bapb-results-container">
              {/* Results toolbar */}
              <div className="bapb-results-toolbar">
                <div className="flex items-center gap-3">
                  <span className="text-[13px] font-medium text-slate-600">
                    {results.length > 0
                      ? <>{results.length.toLocaleString()}{results.length >= MAX_RESULTS ? "+" : ""} <span className="text-slate-400">results</span></>
                      : <span className="text-slate-400">No results</span>
                    }
                  </span>
                  {results.length > 0 && (
                    <span className="text-[11px] text-slate-400">
                      Page {pageSafe} of {totalPages}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {/* View mode toggle */}
                  <div className="bapb-view-toggle">
                    <button
                      onClick={() => setViewMode("table")}
                      className={`bapb-view-btn ${viewMode === "table" ? "bapb-view-btn-active" : ""}`}
                      title="Table view"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3h18v18H3zM3 9h18M3 15h18M9 3v18"/></svg>
                    </button>
                    <button
                      onClick={() => setViewMode("cards")}
                      className={`bapb-view-btn ${viewMode === "cards" ? "bapb-view-btn-active" : ""}`}
                      title="Card view"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
                    </button>
                  </div>

                  {/* Column picker (table mode only) */}
                  {viewMode === "table" && (
                    <div className="relative">
                      <button
                        onClick={() => setShowColPicker(!showColPicker)}
                        className="bapb-btn-ghost bapb-btn-sm"
                        title="Toggle columns"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18M15 3v18"/></svg>
                        Columns
                      </button>
                      {showColPicker && (
                        <div className="bapb-col-dropdown">
                          {Object.entries({
                            statusCol: "Status",
                            assessor: "Assessor",
                            consultant: "Consultant",
                            client: "Client",
                            city: "City",
                            state: "State",
                            cadCode: "CAD Code",
                            siteName: "Site",
                            units: "Units",
                          }).map(([k, label]) => (
                            <label key={k} className="bapb-col-option">
                              <input type="checkbox" checked={cols[k]} onChange={() => toggleCol(k)} className="bapb-checkbox" />
                              {label}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {results.length > 0 && (
                    <button className="bapb-btn-ghost bapb-btn-sm" onClick={exportResults} disabled={isExporting}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      {isExporting ? "Exporting..." : "Export"}
                    </button>
                  )}

                  {nextCursor && results.length < MAX_RESULTS && (
                    <button className="bapb-btn-ghost bapb-btn-sm" onClick={loadMore} disabled={isLoadingMore}>
                      {isLoadingMore ? <><span className="bapb-spinner" /> Loading...</> : "Load more"}
                    </button>
                  )}
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="bapb-error">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                  {error}
                </div>
              )}

              {/* Empty state */}
              {results.length === 0 && !error && (
                <div className="bapb-empty">
                  <div className="bapb-empty-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                  </div>
                  <div className="text-[15px] text-slate-500 font-medium">Search for packets</div>
                  <div className="text-[13px] text-slate-400 mt-1">Enter a search term or use filters, then hit Search</div>
                  <div className="text-[11px] text-slate-400 mt-3">
                    Press <kbd className="bapb-kbd-inline">/</kbd> to focus search
                  </div>
                </div>
              )}

              {/* TABLE VIEW */}
              {results.length > 0 && viewMode === "table" && (
                <div className="bapb-table-wrap">
                  <table className="bapb-table">
                    <thead>
                      <tr>
                        <th>Account</th>
                        <th>Property</th>
                        <th>Year</th>
                        {cols.statusCol && <th>Status</th>}
                        {cols.assessor && <th>Assessor</th>}
                        {cols.consultant && <th>Consultant</th>}
                        {cols.client && <th>Client</th>}
                        {cols.city && <th>City</th>}
                        {cols.state && <th>State</th>}
                        {cols.cadCode && <th>CAD Code</th>}
                        {cols.siteName && <th>Site</th>}
                        {cols.units && <th>Units</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.map((r) => {
                        const id = r.id || r.packet_id;
                        const cover = r._coverMeta || {};
                        const sc = STATUS_COLORS[r.status] || STATUS_COLORS.Draft;
                        return (
                          <tr key={id} onClick={() => openPacket(r)} className="bapb-table-row">
                            <td className="font-medium text-slate-800 font-mono text-[13px]">{r.account_number || "--"}</td>
                            <td className="text-slate-700">{r.title || cover.site_name || "--"}</td>
                            <td className="text-slate-500 tabular-nums">{r.tax_year || "--"}</td>
                            {cols.statusCol && (
                              <td>
                                <span className={`bapb-status-chip ${sc.bg} ${sc.text}`}>
                                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                                  {r.status || "--"}
                                </span>
                              </td>
                            )}
                            {cols.assessor && <td className="text-slate-500">{cover.assessor || "--"}</td>}
                            {cols.consultant && <td className="text-slate-500">{cover.consultant || "--"}</td>}
                            {cols.client && <td className="text-slate-500">{cover.client_name || "--"}</td>}
                            {cols.city && <td className="text-slate-500">{cover.city || "--"}</td>}
                            {cols.state && <td className="text-slate-500">{cover.state || "--"}</td>}
                            {cols.cadCode && <td className="text-slate-500 font-mono text-[12px]">{cover.cad_location_code || "--"}</td>}
                            {cols.siteName && <td className="text-slate-500">{cover.site_name || "--"}</td>}
                            {cols.units && <td className="text-slate-500 tabular-nums">{cover.units || "--"}</td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* CARD VIEW */}
              {results.length > 0 && viewMode === "cards" && (
                <div className="bapb-cards-grid">
                  {pageRows.map((r) => {
                    const id = r.id || r.packet_id;
                    const cover = r._coverMeta || {};
                    const sc = STATUS_COLORS[r.status] || STATUS_COLORS.Draft;
                    return (
                      <div key={id} onClick={() => openPacket(r)} className="bapb-card">
                        <div className="flex items-start justify-between mb-2">
                          <span className="font-mono text-[13px] font-semibold text-slate-800">{r.account_number || "--"}</span>
                          <span className={`bapb-status-chip ${sc.bg} ${sc.text}`}>
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                            {r.status || "Draft"}
                          </span>
                        </div>
                        <div className="text-[14px] font-medium text-slate-700 mb-1 truncate">{r.title || cover.site_name || "Untitled"}</div>
                        <div className="text-[12px] text-slate-400 mb-3">{r.tax_year || "--"}</div>
                        <div className="bapb-card-meta">
                          {cover.assessor && <div><span className="text-slate-400">Assessor</span> <span className="text-slate-600">{cover.assessor}</span></div>}
                          {cover.consultant && <div><span className="text-slate-400">Consultant</span> <span className="text-slate-600">{cover.consultant}</span></div>}
                          {cover.client_name && <div><span className="text-slate-400">Client</span> <span className="text-slate-600">{cover.client_name}</span></div>}
                          {cover.city && <div><span className="text-slate-400">City</span> <span className="text-slate-600">{cover.city}{cover.state ? `, ${cover.state}` : ""}</span></div>}
                        </div>
                        <div className="bapb-card-arrow">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {results.length > 0 && totalPages > 1 && (
                <div className="bapb-pagination">
                  <button
                    className="bapb-page-btn"
                    onClick={() => setPage(1)}
                    disabled={pageSafe <= 1}
                    title="First page"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="11 17 6 12 11 7"/><polyline points="18 17 13 12 18 7"/></svg>
                  </button>
                  <button
                    className="bapb-page-btn"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={pageSafe <= 1}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>

                  <div className="bapb-page-numbers">
                    {generatePageNumbers(pageSafe, totalPages).map((n, i) =>
                      n === "..." ? (
                        <span key={`dots-${i}`} className="text-slate-300 px-1">...</span>
                      ) : (
                        <button
                          key={n}
                          onClick={() => setPage(n)}
                          className={`bapb-page-num ${n === pageSafe ? "bapb-page-num-active" : ""}`}
                        >
                          {n}
                        </button>
                      )
                    )}
                  </div>

                  <button
                    className="bapb-page-btn"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={pageSafe >= totalPages}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                  <button
                    className="bapb-page-btn"
                    onClick={() => setPage(totalPages)}
                    disabled={pageSafe >= totalPages}
                    title="Last page"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="13 17 18 12 13 7"/><polyline points="6 17 11 12 6 7"/></svg>
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {mode === "import" && (
          <div className="bapb-panel">
            <ImportWizard
              onDone={(result) => {
                if (result?.id) {
                  setPacket(result);
                  setMode("builder");
                  return;
                }

                if (result?.summary) {
                  const s = result.summary;
                  alert(
                    `Bulk Import complete:\n` +
                      `Created: ${s.created}\n` +
                      `Skipped existing: ${s.skippedExisting}\n` +
                      `Skipped invalid: ${s.skippedInvalid}\n` +
                      `Failed: ${s.failed}`
                  );
                }

                setMode("home");
                setNeedsRequery(true);
              }}
            />
          </div>
        )}

        {mode === "builder" && <PacketBuilder key={packet?.id || "new"} packet={packet} />}
      </main>

      {/* ══════ STYLES ══════ */}
      <style>{`
        /* ── Root ── */
        .bapb-root {
          min-height: 100vh;
          background: #f9fafb;
          color: #1f2937;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
        }

        /* ── Header ── */
        .bapb-header {
          position: sticky;
          top: 0;
          z-index: 40;
          background: rgba(255,255,255,0.80);
          backdrop-filter: blur(20px) saturate(180%);
          border-bottom: 1px solid #e5e7eb;
        }
        .bapb-header-inner {
          max-width: 1200px;
          margin: 0 auto;
          padding: 12px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        @media (min-width: 1280px) { .bapb-header-inner { max-width: 1400px; } }
        @media (min-width: 1536px) { .bapb-header-inner { max-width: 1600px; } }
        @media (min-width: 1920px) { .bapb-header-inner { max-width: 1850px; } }
        .bapb-logo-img {
          height: 32px;
          width: auto;
          object-fit: contain;
        }
        .bapb-header-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 500;
          color: #6b7280;
          background: white;
          border: 1px solid #e5e7eb;
          transition: all 0.15s;
          cursor: pointer;
        }
        .bapb-header-btn:hover {
          color: #374151;
          border-color: #d1d5db;
        }

        /* ── Main ── */
        .bapb-main {
          max-width: 1200px;
          margin: 0 auto;
          padding: 24px;
        }
        @media (min-width: 1280px) { .bapb-main { max-width: 1400px; } }
        @media (min-width: 1536px) { .bapb-main { max-width: 1600px; } }
        @media (min-width: 1920px) { .bapb-main { max-width: 1850px; } }

        /* ── Command Bar ── */
        .bapb-command-bar {
          margin-bottom: 20px;
        }
        .bapb-search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .bapb-search-icon {
          position: absolute;
          left: 16px;
          color: #9ca3af;
          pointer-events: none;
        }
        .bapb-search-input {
          width: 100%;
          padding: 14px 80px 14px 48px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          color: #1f2937;
          font-size: 15px;
          outline: none;
          transition: all 0.2s;
        }
        .bapb-search-input::placeholder { color: #9ca3af; }
        .bapb-search-input:focus {
          border-color: #93c5fd;
          box-shadow: 0 0 0 3px rgba(147,197,253,0.2);
        }
        .bapb-search-clear {
          position: absolute;
          right: 48px;
          color: #9ca3af;
          cursor: pointer;
          padding: 4px;
          border-radius: 6px;
          background: none;
          border: none;
        }
        .bapb-search-clear:hover { color: #6b7280; }
        .bapb-kbd {
          position: absolute;
          right: 14px;
          padding: 2px 8px;
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 12px;
          font-family: monospace;
          color: #9ca3af;
          pointer-events: none;
        }
        .bapb-kbd-inline {
          padding: 1px 6px;
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          font-size: 11px;
          font-family: monospace;
          color: #6b7280;
        }

        /* ── Action row ── */
        .bapb-action-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 12px;
          flex-wrap: wrap;
          gap: 8px;
        }

        /* ── Buttons ── */
        .bapb-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          color: #1d4ed8;
          background: #eff6ff;
          border: 1px solid #93c5fd;
          cursor: pointer;
          transition: all 0.2s;
        }
        .bapb-btn-primary:hover {
          background: #dbeafe;
          border-color: #60a5fa;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(147,197,253,0.3);
        }
        .bapb-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }
        .bapb-btn-accent {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          color: #1d4ed8;
          background: #eff6ff;
          border: 1px solid #93c5fd;
          cursor: pointer;
          transition: all 0.2s;
        }
        .bapb-btn-accent:hover {
          background: #dbeafe;
          border-color: #60a5fa;
        }
        .bapb-btn-ghost {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 500;
          color: #6b7280;
          background: white;
          border: 1px solid #e5e7eb;
          cursor: pointer;
          transition: all 0.15s;
        }
        .bapb-btn-ghost:hover {
          color: #374151;
          border-color: #d1d5db;
        }
        .bapb-btn-ghost-active {
          color: #1d4ed8 !important;
          background: #eff6ff !important;
          border-color: #93c5fd !important;
        }
        .bapb-btn-sm { padding: 5px 10px; font-size: 12px; }

        .bapb-filter-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 18px;
          height: 18px;
          border-radius: 9px;
          background: #3b82f6;
          color: white;
          font-size: 10px;
          font-weight: 700;
          padding: 0 5px;
        }

        /* ── Panels ── */
        .bapb-panel {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 20px;
        }
        .bapb-panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
        }
        .bapb-panel-close {
          color: #9ca3af;
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 6px;
          transition: all 0.15s;
        }
        .bapb-panel-close:hover { color: #6b7280; background: #f3f4f6; }

        .bapb-slide-down {
          animation: bapbSlideDown 0.2s ease-out;
        }
        @keyframes bapbSlideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* ── Inputs ── */
        .bapb-label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 6px;
        }
        .bapb-input {
          width: 100%;
          padding: 9px 12px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          color: #1f2937;
          font-size: 13px;
          outline: none;
          transition: all 0.15s;
        }
        .bapb-input::placeholder { color: #9ca3af; }
        .bapb-input:focus {
          border-color: #93c5fd;
          box-shadow: 0 0 0 2px rgba(147,197,253,0.2);
        }

        /* ── Status pills ── */
        .bapb-status-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          border: none;
          cursor: pointer;
          transition: all 0.15s;
        }

        /* ── Results ── */
        .bapb-results-container {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          overflow: hidden;
        }
        .bapb-results-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 20px;
          border-bottom: 1px solid #e5e7eb;
          flex-wrap: wrap;
          gap: 8px;
        }

        /* ── View toggle ── */
        .bapb-view-toggle {
          display: inline-flex;
          background: #f3f4f6;
          border-radius: 8px;
          padding: 2px;
          border: 1px solid #e5e7eb;
        }
        .bapb-view-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 26px;
          border-radius: 6px;
          border: none;
          background: transparent;
          color: #9ca3af;
          cursor: pointer;
          transition: all 0.15s;
        }
        .bapb-view-btn:hover { color: #6b7280; }
        .bapb-view-btn-active {
          background: white !important;
          color: #1d4ed8 !important;
          box-shadow: 0 1px 2px rgba(0,0,0,0.06);
        }

        /* ── Column picker ── */
        .bapb-col-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          right: 0;
          z-index: 50;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 8px;
          min-width: 160px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.08);
          animation: bapbSlideDown 0.15s ease-out;
        }
        .bapb-col-option {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 8px;
          border-radius: 6px;
          font-size: 12px;
          color: #4b5563;
          cursor: pointer;
          transition: background 0.1s;
        }
        .bapb-col-option:hover { background: #f3f4f6; }
        .bapb-checkbox {
          accent-color: #3b82f6;
          width: 14px;
          height: 14px;
        }

        /* ── Table ── */
        .bapb-table-wrap {
          overflow-x: auto;
        }
        .bapb-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }
        .bapb-table th {
          padding: 10px 16px;
          text-align: left;
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 1px solid #e5e7eb;
          white-space: nowrap;
          position: sticky;
          top: 0;
          background: white;
        }
        .bapb-table td {
          padding: 12px 16px;
          white-space: nowrap;
          border-bottom: 1px solid #f3f4f6;
        }
        .bapb-table-row {
          cursor: pointer;
          transition: background 0.1s;
        }
        .bapb-table-row:hover {
          background: #f9fafb !important;
        }

        /* ── Status chip (in table/cards) ── */
        .bapb-status-chip {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          white-space: nowrap;
        }

        /* ── Cards ── */
        .bapb-cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 12px;
          padding: 16px;
        }
        .bapb-card {
          position: relative;
          padding: 16px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .bapb-card:hover {
          border-color: #d1d5db;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.06);
        }
        .bapb-card-meta {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4px;
          font-size: 11px;
        }
        .bapb-card-meta div {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }
        .bapb-card-arrow {
          position: absolute;
          bottom: 14px;
          right: 14px;
          color: #d1d5db;
          transition: all 0.2s;
        }
        .bapb-card:hover .bapb-card-arrow {
          color: #6b7280;
          transform: translateX(3px);
        }

        /* ── Pagination ── */
        .bapb-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          padding: 14px 20px;
          border-top: 1px solid #e5e7eb;
        }
        .bapb-page-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: none;
          background: transparent;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.1s;
        }
        .bapb-page-btn:hover:not(:disabled) { background: #f3f4f6; color: #1f2937; }
        .bapb-page-btn:disabled { opacity: 0.25; cursor: not-allowed; }
        .bapb-page-numbers {
          display: flex;
          align-items: center;
          gap: 2px;
          margin: 0 4px;
        }
        .bapb-page-num {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 32px;
          height: 32px;
          border-radius: 8px;
          border: none;
          background: transparent;
          color: #6b7280;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.1s;
        }
        .bapb-page-num:hover { background: #f3f4f6; color: #1f2937; }
        .bapb-page-num-active {
          background: #eff6ff !important;
          color: #1d4ed8 !important;
          font-weight: 600;
          border: 1px solid #93c5fd;
        }

        /* ── Empty state ── */
        .bapb-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 64px 24px;
        }
        .bapb-empty-icon {
          color: #d1d5db;
          margin-bottom: 16px;
        }

        /* ── Error ── */
        .bapb-error {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 12px 16px;
          padding: 12px 16px;
          border-radius: 12px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #b91c1c;
          font-size: 13px;
        }

        /* ── Spinner ── */
        .bapb-spinner {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid rgba(29,78,216,0.2);
          border-top-color: #1d4ed8;
          border-radius: 50%;
          animation: bapbSpin 0.6s linear infinite;
        }
        @keyframes bapbSpin {
          to { transform: rotate(360deg); }
        }

        /* ── Scrollbar ── */
        .bapb-root ::-webkit-scrollbar { width: 6px; height: 6px; }
        .bapb-root ::-webkit-scrollbar-track { background: transparent; }
        .bapb-root ::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }
        .bapb-root ::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
      `}</style>
    </div>
  );
}

/* ── Page number generator ── */
function generatePageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  pages.push(1);
  if (current > 3) pages.push("...");
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }
  if (current < total - 2) pages.push("...");
  pages.push(total);
  return pages;
}
